import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { SESSION_EVENT, EVENT } from '../consts';
import { normalizeReportData } from '../reportingUtils';
import { GET_AB_TEST, GET_REPORTING_BY_DATE_AB_TEST } from '../../../queries';

export const useReportingData = (testId, translate) => {
    const [dateRange, setDateRange] = useState({
        start: null,
        end: null,
    });
    const [selectedEvent, setSelectedEvent] = useState(SESSION_EVENT);
    const [eventKind, setEventKind] = useState(EVENT);
    const [isInitialized, setIsInitialized] = useState(false);
    const { data: testData, loading: isLoadingTest, error: testError } = useQuery(GET_AB_TEST, {
        skip: !testId,
        fetchPolicy: 'cache-first',
        variables: { testId },
    });
    const { data: reportData, loading: isLoadingReport, error: reportError } = useQuery(GET_REPORTING_BY_DATE_AB_TEST, {
        skip: !testId,
        variables: { testId },
    });
    const normalizedData = useMemo(() => {
        if (!testData?.abTest) return null;

        return normalizeReportData(
            translate,
            testData.abTest,
            dateRange.start,
            dateRange.end,
            reportData?.abTestsReportByDate || [],
            selectedEvent.value
        );
    }, [testData, dateRange.start, dateRange.end, translate, selectedEvent, reportData]);

    useEffect(() => {
        if (isInitialized || !normalizedData) return;

        const today = new Date();
        const endDate = normalizedData.endDate ? new Date(normalizedData.endDate) : today;
        const minEndDate = endDate.getTime() < today.getTime() ? endDate : today;

        setDateRange({
            start: new Date(normalizedData.startDate),
            end: minEndDate,
        });
        setIsInitialized(true);
    }, [normalizedData, isInitialized]);

    const updateDateRange = newRange => {
        setDateRange({
            start: newRange.start_date,
            end: newRange.end_date,
        });
    };

    const updateSelectedEvent = event => {
        setSelectedEvent(event);
    };

    const toggleEventUnique = newVal => {
        if (newVal !== eventKind) {
            setEventKind(newVal);
        }
    };

    return {
        normalizedData,
        dateRange,
        selectedEvent,
        eventKind,
        isLoading: isLoadingTest || isLoadingReport,
        error: testError || reportError,
        isEmpty: !isInitialized || !normalizedData?.tableData || Object.keys(normalizedData.tableData).length === 0,
        handlers: {
            updateDateRange,
            updateSelectedEvent,
            toggleEventUnique,
        },
    };
};
